import React, { useState } from 'react';
import { QuestionDefinition, QuestionOption } from '../../../types/survey.types';
import { useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { DndContext, closestCenter, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { arrayMove } from '@dnd-kit/sortable';

interface BasicSettingsProps {
  question: QuestionDefinition;
  onChange: (question: QuestionDefinition) => void;
}

interface SortableOptionProps {
  option: QuestionOption;
  index: number;
  onUpdate: (optionId: string, field: 'label' | 'value', value: string) => void;
  onDelete: (optionId: string) => void;
  canDelete: boolean;
}

const SortableOption: React.FC<SortableOptionProps> = ({ option, index, onUpdate, onDelete, canDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        fontFamily: 'var(--font-body)',
      }}
      className="flex items-center gap-2 py-1.5"
    >
      <div
        {...attributes}
        {...listeners}
        className="flex-shrink-0 cursor-move transition-colors"
        style={{ color: 'var(--color-muted)' }}
        title="Drag to reorder"
        onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-primary)'}
        onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-muted)'}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
        </svg>
      </div>

      <div
        className="flex-shrink-0 flex items-center justify-center"
        style={{
          width: '1.5rem',
          height: '1.5rem',
          fontSize: '0.75rem',
          fontWeight: 600,
          color: 'var(--color-accent)',
        }}
      >
        {index + 1}
      </div>

      <div className="flex-1">
        <input
          type="text"
          value={option.label}
          onChange={(e) => onUpdate(option.id, 'label', e.target.value)}
          className="w-full px-2.5 py-1.5 text-sm border rounded transition-all outline-none"
          style={{
            fontFamily: 'var(--font-body)',
            color: 'var(--color-primary)',
            borderColor: 'var(--color-border)',
            backgroundColor: 'white',
          }}
          placeholder="Enter option text..."
        />
      </div>

      <button
        onClick={() => onDelete(option.id)}
        disabled={!canDelete}
        className="flex-shrink-0 rounded transition-colors flex items-center justify-center"
        style={{
          width: '1.75rem',
          height: '1.75rem',
          color: canDelete ? '#dc2626' : 'var(--color-muted)',
          cursor: canDelete ? 'pointer' : 'not-allowed',
          opacity: canDelete ? 1 : 0.4,
        }}
        onMouseEnter={(e) => {
          if (canDelete) {
            e.currentTarget.style.backgroundColor = 'rgba(220, 38, 38, 0.1)';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
        title={canDelete ? 'Delete option' : 'Minimum 2 options required'}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

const BasicSettings: React.FC<BasicSettingsProps> = ({ question, onChange }) => {
  const [bulkText, setBulkText] = useState('');
  const [showBulkAdd, setShowBulkAdd] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handlePlaceholderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...question, placeholder: e.target.value });
  };

  // Options management
  const options = question.options || [];

  const addOption = () => {
    const newOption: QuestionOption = {
      id: `option_${Date.now()}`,
      label: `Option ${options.length + 1}`,
      value: `option_${options.length + 1}`,
    };

    onChange({
      ...question,
      options: [...options, newOption],
    });
  };

  const updateOption = (optionId: string, field: 'label' | 'value', value: string) => {
    onChange({
      ...question,
      options: options.map((opt) =>
        opt.id === optionId
          ? {
              ...opt,
              label: value,
              value: value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
            }
          : opt
      ),
    });
  };

  const deleteOption = (optionId: string) => {
    if (options.length <= 2) return;

    onChange({
      ...question,
      options: options.filter((opt) => opt.id !== optionId),
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = options.findIndex((opt) => opt.id === active.id);
      const newIndex = options.findIndex((opt) => opt.id === over.id);

      onChange({
        ...question,
        options: arrayMove(options, oldIndex, newIndex),
      });
    }
  };

  const handleBulkAdd = () => {
    const lines = bulkText.split('\n').filter((line) => line.trim());
    if (lines.length === 0) return;

    const newOptions: QuestionOption[] = lines.map((line, index) => ({
      id: `option_${Date.now()}_${index}`,
      label: line.trim(),
      value: line.trim().toLowerCase().replace(/\s+/g, '_'),
    }));

    onChange({
      ...question,
      options: [...options, ...newOptions],
    });

    setBulkText('');
    setShowBulkAdd(false);
  };

  const toggleOtherOption = (checked: boolean) => {
    onChange({
      ...question,
      settings: { ...question.settings, showOtherOption: checked },
    });
  };

  const shouldShowOptions = ['radio', 'checkbox', 'dropdown'].includes(question.type);


  return (
    <div className="space-y-4">
      {/* Question Title */}
      <div>
        <label className="block text-sm font-semibold mb-1.5" style={{ fontFamily: 'var(--font-body)', color: 'var(--color-primary)' }}>
          Question Title <span style={{ color: '#dc2626' }}>*</span>
        </label>
        <textarea
          value={question.title}
          onChange={(e) => onChange({ ...question, title: e.target.value })}
          rows={2}
          className="w-full px-3 py-2 text-sm border rounded transition-all outline-none resize-y"
          style={{
            fontFamily: 'var(--font-body)',
            color: 'var(--color-primary)',
            borderColor: 'var(--color-border)',
            backgroundColor: 'white',
            minHeight: '3rem',
          }}
          placeholder="Enter your question here"
        />
      </div>

      {/* Placeholder Text - Only show for applicable question types */}
      {['text', 'textarea', 'email', 'number'].includes(question.type) && (
        <div>
          <label className="block text-sm font-semibold mb-1.5" style={{ fontFamily: 'var(--font-body)', color: 'var(--color-primary)' }}>
            Placeholder Text <span style={{ fontWeight: 'normal', color: 'var(--color-muted)' }}>(optional)</span>
          </label>
          <input
            type="text"
            value={question.placeholder || ''}
            onChange={handlePlaceholderChange}
            className="w-full px-3 py-2 text-sm border rounded transition-all outline-none"
            style={{
              fontFamily: 'var(--font-body)',
              color: 'var(--color-primary)',
              borderColor: 'var(--color-border)',
              backgroundColor: 'white',
            }}
            placeholder="e.g., Enter your answer here..."
          />
          <p className="mt-1.5 text-xs" style={{ fontFamily: 'var(--font-body)', color: 'var(--color-muted)' }}>
            Shown inside the input field before the user starts typing.
          </p>
        </div>
      )}

      {/* Options Section - For radio, checkbox, dropdown */}
      {shouldShowOptions && (
        <div className="pt-3" style={{ borderTop: '1px solid var(--color-border)' }}>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-semibold" style={{ fontFamily: 'var(--font-body)', color: 'var(--color-primary)' }}>
              Options
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setShowBulkAdd(!showBulkAdd)}
                className="px-3 py-1.5 text-sm border rounded transition-colors"
                style={{
                  fontFamily: 'var(--font-body)',
                  fontWeight: 600,
                  color: 'var(--color-accent)',
                  borderColor: 'rgba(199, 161, 74, 0.3)',
                  backgroundColor: 'transparent',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(199, 161, 74, 0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                {showBulkAdd ? 'Cancel Bulk Add' : 'Bulk Add'}
              </button>
              <button
                onClick={addOption}
                className="px-3 py-1.5 text-sm rounded transition-colors"
                style={{
                  fontFamily: 'var(--font-body)',
                  fontWeight: 600,
                  backgroundColor: 'var(--color-accent)',
                  color: 'white',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-accent-hover)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-accent)'}
              >
                + Add Option
              </button>
            </div>
          </div>

          {/* Bulk Add Interface */}
          {showBulkAdd && (
            <div className="mb-3 p-3 border rounded" style={{ backgroundColor: 'rgba(199, 161, 74, 0.05)', borderColor: 'rgba(199, 161, 74, 0.2)' }}>
              <label className="block text-sm font-semibold mb-2" style={{ fontFamily: 'var(--font-body)', color: 'var(--color-primary)' }}>
                Bulk Add Options (one per line)
              </label>
              <textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                rows={5}
                className="w-full px-3 py-2 text-sm border rounded transition-all outline-none resize-none"
                style={{
                  fontFamily: 'var(--font-body)',
                  color: 'var(--color-primary)',
                  borderColor: 'var(--color-border)',
                  backgroundColor: 'white',
                }}
                placeholder="Option 1&#10;Option 2&#10;Option 3"
              />
              <div className="mt-2 flex justify-end">
                <button
                  onClick={handleBulkAdd}
                  disabled={!bulkText.trim()}
                  className="px-4 py-2 text-sm rounded transition-colors"
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontWeight: 600,
                    backgroundColor: bulkText.trim() ? 'var(--color-accent)' : '#d1d5db',
                    color: 'white',
                    cursor: bulkText.trim() ? 'pointer' : 'not-allowed',
                  }}
                >
                  Add All Options
                </button>
              </div>
            </div>
          )}

          {/* Options with Drag & Drop */}
          <div className="border rounded p-3" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
            {options.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="mb-2">No options yet</p>
                <p className="text-sm">Click "Add Option" to get started</p>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={options.map((o) => o.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-1.5">
                    {options.map((option, index) => (
                      <SortableOption
                        key={option.id}
                        option={option}
                        index={index}
                        onUpdate={updateOption}
                        onDelete={deleteOption}
                        canDelete={options.length > 2}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}

            {options.length < 2 && options.length > 0 && (
              <p className="mt-3 text-xs text-red-600">
                ⚠️ At least 2 options are required
              </p>
            )}
          </div>

          <p className="mt-2 text-xs text-gray-500">
            Drag the handle to reorder options. Option text will be shown to respondents.
          </p>

          {/* Include "Other" Option - for checkbox only */}
          {question.type === 'checkbox' && (
            <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--color-border)' }}>
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => toggleOtherOption(!question.settings?.showOtherOption)}>
                <div className="relative flex-shrink-0" style={{ width: '2.75rem', height: '1.5rem' }}>
                  <div
                    className="absolute inset-0 rounded-full transition-all"
                    style={{
                      backgroundColor: question.settings?.showOtherOption ? 'var(--color-accent)' : '#d1d5db',
                    }}
                  ></div>
                  <div
                    className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow"
                    style={{
                      left: question.settings?.showOtherOption ? '1.375rem' : '0.125rem',
                      transition: 'left 0.2s ease-in-out',
                    }}
                  ></div>
                </div>
                <div className="flex-1">
                  <span className="text-sm font-semibold block" style={{ fontFamily: 'var(--font-body)', color: 'var(--color-primary)' }}>
                    Include "Other" option with text input
                  </span>
                  <p className="text-xs mt-0.5" style={{ fontFamily: 'var(--font-body)', color: 'var(--color-muted)' }}>
                    Allows respondents to enter a custom answer
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Required Toggle */}
      <div className="pt-3" style={{ borderTop: '1px solid var(--color-border)' }}>
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => onChange({ ...question, required: !question.required })}>
          <div className="relative flex-shrink-0" style={{ width: '2.75rem', height: '1.5rem' }}>
            <div
              className="absolute inset-0 rounded-full transition-all"
              style={{
                backgroundColor: question.required ? 'var(--color-accent)' : '#d1d5db',
              }}
            ></div>
            <div
              className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow"
              style={{
                left: question.required ? '1.375rem' : '0.125rem',
                transition: 'left 0.2s ease-in-out',
              }}
            ></div>
          </div>
          <div className="flex-1">
            <span className="text-sm font-semibold block" style={{ fontFamily: 'var(--font-body)', color: 'var(--color-primary)' }}>
              Required Question
            </span>
            <p className="text-xs mt-0.5" style={{ fontFamily: 'var(--font-body)', color: 'var(--color-muted)' }}>
              Respondents must answer this question to continue
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicSettings;
